import { useEffect, useMemo, useRef, useState } from 'react';
import type { PaletteNodeType } from '@repletoryreach/sdk-ui/dnd';

export interface QuickAddProps {
  /** Where to open, in client pixels. Null closes. */
  anchor: { x: number; y: number } | null;
  title: string;
  hint?: string;
  searchLabel: string;
  emptyLabel: string;
  items: PaletteNodeType[];
  onPick: (kind: string) => void;
  onClose: () => void;
}

const WIDTH = 288;
const MAX_HEIGHT = 380;

/**
 * The menu behind every "+": a searchable list of node kinds that opens
 * beside the port, the drop point or the double-click. Keyboard first: type
 * to filter, arrows to move, Enter to add, Escape to close.
 *
 * Fixed-positioned so it escapes the canvas's transform and overflow, and
 * clamped to the viewport so a "+" near the bottom edge still shows the list.
 */
export function QuickAdd({ anchor, title, hint, searchLabel, emptyLabel, items, onPick, onClose }: QuickAddProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const open = anchor !== null;

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    // The input is in a fresh render; focus on the next tick.
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, anchor?.x, anchor?.y]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => `${i.label} ${i.description ?? ''}`.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => { setActive(0); }, [visible.length]);

  if (!open || !anchor) return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const left = Math.min(Math.max(8, anchor.x + 12), vw - WIDTH - 8);
  const top = Math.min(Math.max(8, anchor.y - 12), vh - MAX_HEIGHT - 8);

  const pick = (index: number) => {
    const item = visible[index];
    if (item) onPick(item.type);
  };

  return (
    <>
      {/* Anything outside the menu closes it. */}
      <div className="rr-quickadd__backdrop" onPointerDown={onClose} />
      <div
        className="rr-quickadd"
        style={{ left, top, width: WIDTH, maxHeight: MAX_HEIGHT }}
        role="dialog"
        aria-label={title}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onClose(); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(visible.length - 1, a + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(active); }
        }}
      >
        <div className="rr-quickadd__head">
          <span className="rr-quickadd__title">{title}</span>
          {hint ? <span className="rr-quickadd__hint">{hint}</span> : null}
        </div>
        <input
          ref={inputRef}
          className="rr-quickadd__search"
          type="search"
          value={query}
          placeholder={searchLabel}
          aria-label={searchLabel}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
        <div className="rr-quickadd__list" role="listbox">
          {visible.length === 0 && <div className="rr-quickadd__empty">{emptyLabel}</div>}
          {visible.map((item, i) => (
            <button
              key={item.type}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`rr-quickadd__item${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(i)}
            >
              <span className="rr-quickadd__icon">{item.icon}</span>
              <span className="rr-quickadd__body">
                <span className="rr-quickadd__label">{item.label}</span>
                {item.description ? <span className="rr-quickadd__desc">{item.description}</span> : null}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
